import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  CardContent,
  Avatar,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Business as ClinicIcon,
  LocalHospital as ProviderIcon,
  People as PeopleIcon,
  Assignment as SessionIcon,
  AdminPanelSettings as AdminIcon,
  Badge as StaffIcon,
  Groups as SeenIcon,
  TrendingUp as Trend7Icon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import HoverCard3D from '../components/HoverCard3D';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ADMIN_PORTAL_URL = process.env.REACT_APP_ADMIN_PORTAL_URL || 'http://localhost:3002';

function fmt(n) {
  return n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString();
}

export default function SuperAdminPlatformDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/superadmin/dashboard/stats`)
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedClinics = useMemo(() => {
    const rows = stats?.clinics ? [...stats.clinics] : [];
    rows.sort((a, b) => {
      let av;
      let bv;
      if (orderBy === 'name') {
        av = (a.name || '').toLowerCase();
        bv = (b.name || '').toLowerCase();
      } else {
        av = Number(a[orderBy]) || 0;
        bv = Number(b[orderBy]) || 0;
      }
      const c = av < bv ? -1 : av > bv ? 1 : 0;
      return order === 'asc' ? c : -c;
    });
    return rows;
  }, [stats?.clinics, order, orderBy]);

  const handleSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const summaryCards = [
    { label: 'Clinics (active / total)', value: stats ? `${fmt(stats.active_clinics)} / ${fmt(stats.total_clinics)}` : '—', icon: <ClinicIcon />, color: '#0d9488' },
    { label: 'Providers', value: fmt(stats?.total_providers), icon: <ProviderIcon />, color: '#1e40af' },
    { label: 'Staff', value: fmt(stats?.total_staff), icon: <StaffIcon />, color: '#0f766e' },
    { label: 'Clinic admins', value: fmt(stats?.total_clinic_admins), icon: <AdminIcon />, color: '#7c3aed' },
    { label: 'Patients (roster)', value: fmt(stats?.total_patients), icon: <PeopleIcon />, color: '#047857' },
    { label: 'Visits (sessions)', value: fmt(stats?.total_sessions), icon: <SessionIcon />, color: '#b45309' },
    { label: 'Patients seen (distinct)', value: fmt(stats?.patients_with_visits), icon: <SeenIcon />, color: '#0369a1' },
    { label: 'Sessions 7d / 30d', value: stats ? `${fmt(stats.sessions_last_7_days)} / ${fmt(stats.sessions_last_30_days)}` : '—', icon: <Trend7Icon />, color: '#be123c' },
  ];

  const statusEntries = stats?.sessions_by_status
    ? Object.entries(stats.sessions_by_status).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontWeight: 700,
              color: '#042f2e',
              mb: 1,
            }}
          >
            Platform overview
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, maxWidth: 640 }}>
            You are signed in as <strong>Super Admin</strong>. This view loads cross-clinic analytics from the platform API.
            The provider portal’s patient and scribe features are for clinic staff only — use the{' '}
            <strong>Platform admin</strong> app to manage clinics, providers, and patients across the organization.
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            endIcon={<OpenInNewIcon />}
            href={ADMIN_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              py: 1.5,
              fontWeight: 700,
              bgcolor: '#042f2e',
              '&:hover': { bgcolor: '#134e4a' },
            }}
          >
            Open Platform admin
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
            Full management UI (default {ADMIN_PORTAL_URL})
          </Typography>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !stats && (
        <Typography color="error" sx={{ mb: 2 }}>
          Could not load platform statistics. Confirm the API is running and your account has super admin access.
        </Typography>
      )}

      {!loading && stats && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summaryCards.map((c) => (
              <Grid item xs={12} sm={6} md={3} key={c.label}>
                <HoverCard3D
                  cardSx={{
                    background: 'linear-gradient(160deg, #ffffff 0%, #f0fdfa 100%)',
                    border: '1px solid rgba(13, 148, 136, 0.12)',
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <Avatar sx={{ bgcolor: `${c.color}22`, color: c.color, mr: 2, width: 48, height: 48 }}>{c.icon}</Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight={800}>
                        {c.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {c.label}
                      </Typography>
                    </Box>
                  </CardContent>
                </HoverCard3D>
              </Grid>
            ))}
          </Grid>

          <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid rgba(4, 47, 46, 0.1)' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Sessions by status (all time)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {statusEntries.length === 0 && <Typography variant="body2" color="text.secondary">No sessions.</Typography>}
              {statusEntries.map(([key, count]) => (
                <Chip key={key} size="small" label={`${key}: ${fmt(count)}`} variant="outlined" />
              ))}
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid rgba(4, 47, 46, 0.1)', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(13, 148, 136, 0.06)' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Clinics — roster & activity
              </Typography>
            </Box>
            <TableContainer sx={{ maxWidth: '100%' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy === 'name' ? order : false}>
                      <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                        Organization
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel active={orderBy === 'provider_count'} direction={orderBy === 'provider_count' ? order : 'asc'} onClick={() => handleSort('provider_count')}>
                        Providers
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel active={orderBy === 'staff_count'} direction={orderBy === 'staff_count' ? order : 'asc'} onClick={() => handleSort('staff_count')}>
                        Staff
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel active={orderBy === 'patient_count'} direction={orderBy === 'patient_count' ? order : 'asc'} onClick={() => handleSort('patient_count')}>
                        Patients
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'visit_sessions_count'}
                        direction={orderBy === 'visit_sessions_count' ? order : 'asc'}
                        onClick={() => handleSort('visit_sessions_count')}
                      >
                        Visits
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'patients_seen_count'}
                        direction={orderBy === 'patients_seen_count' ? order : 'asc'}
                        onClick={() => handleSort('patients_seen_count')}
                      >
                        Seen
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedClinics.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography fontWeight={700}>{row.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(row.type || 'clinic').replace(/_/g, ' ')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{fmt(row.provider_count)}</TableCell>
                      <TableCell align="right">{fmt(row.staff_count)}</TableCell>
                      <TableCell align="right">{fmt(row.patient_count)}</TableCell>
                      <TableCell align="right">{fmt(row.visit_sessions_count)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Distinct patients with ≥1 session">
                          <span>{fmt(row.patients_seen_count)}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
